import { apiClient } from '@/lib/api-client';
import type { PaginatedResponse } from '@/types/contractor';
import type {
  CreateLenderInput,
  Lender,
  LenderQueryParams,
  UpdateLenderInput,
} from '@/types/lender';

// API wraps responses in result object
interface ApiWrapper<T> {
  result: T;
  status: number;
  message: string;
  error: null | { code: number; message: string; issues: unknown[] };
}

export const lenderService = {
  /**
   * Get all lenders with pagination and filters
   */
  async getAll(params?: LenderQueryParams): Promise<PaginatedResponse<Lender>> {
    const queryParams = new URLSearchParams();

    if (params?.page) queryParams.set('page', String(params.page));
    if (params?.limit) queryParams.set('limit', String(params.limit));
    if (params?.sortBy) queryParams.set('sortBy', params.sortBy);
    if (params?.sortOrder) queryParams.set('sortOrder', params.sortOrder);
    if (params?.status) queryParams.set('status', params.status);
    if (params?.minRating) queryParams.set('minRating', String(params.minRating));
    if (params?.loanType) queryParams.set('loanType', params.loanType);
    if (params?.search) queryParams.set('search', params.search);

    const queryString = queryParams.toString();
    const endpoint = `/lenders${queryString ? `?${queryString}` : ''}`;

    const response = await apiClient.get<ApiWrapper<PaginatedResponse<Lender>>>(endpoint);
    return response.result;
  },

  /**
   * Get active lenders (for dropdowns)
   */
  async getActive(): Promise<Lender[]> {
    const response = await apiClient.get<ApiWrapper<Lender[]>>('/lenders/active');
    return response.result;
  },

  /**
   * Get lender by ID
   */
  async getById(id: string): Promise<Lender> {
    const response = await apiClient.get<ApiWrapper<Lender>>(`/lenders/${id}`);
    return response.result;
  },

  /**
   * Create a new lender
   */
  async create(data: CreateLenderInput): Promise<Lender> {
    const response = await apiClient.post<ApiWrapper<Lender>>('/lenders', data);
    return response.result;
  },

  /**
   * Update a lender
   */
  async update(id: string, data: UpdateLenderInput): Promise<Lender> {
    const response = await apiClient.put<ApiWrapper<Lender>>(`/lenders/${id}`, data);
    return response.result;
  },

  /**
   * Delete (soft delete) a lender
   */
  async delete(id: string): Promise<void> {
    await apiClient.delete(`/lenders/${id}`);
  },
};
